import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, map, catchError, of } from 'rxjs';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import { getTimeUntilExpiration } from '../utils/jwt.utils';

export interface User {
  id: string;
  email: string;
  displayName?: string;
  preferredLanguage: string;
  emailVerified: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AuthResponse {
  userId: string;
  token: string;
  user: User;
}


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.currentUser$.pipe(map(user => !!user));
  private refreshTimer: any = null;
  private isRefreshing = false;

  constructor(
    private api: ApiService,
    private storage: StorageService
  ) {
    // Initialize user from storage and validate token
    this.initializeAuth();
  }

  /**
   * Initialize authentication state and set up auto-refresh
   */
  private initializeAuth(): void {
    const savedUser = this.storage.getUser();
    const token = this.storage.getToken();

    if (savedUser && token) {
      // Check if token is expired
      if (this.storage.isTokenExpired()) {
        console.log('Token expired on init, attempting refresh...');
        this.refreshToken().subscribe({
          next: () => {
            console.log('Token refreshed successfully on init');
            this.currentUserSubject.next(savedUser);
            this.scheduleTokenRefresh();
          },
          error: () => {
            console.log('Token refresh failed on init, logging out');
            this.logout();
          }
        });
      } else {
        // Token is valid, set user and schedule refresh
        this.currentUserSubject.next(savedUser);
        this.scheduleTokenRefresh();
      }
    }
  }

  /**
   * Schedule automatic token refresh before expiration
   */
  private scheduleTokenRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const token = this.storage.getToken();
    if (!token) {
      return;
    }

    // Get time until expiration and schedule refresh 1 hour before
    const timeUntilExpiration = getTimeUntilExpiration(token);
    const refreshBuffer = 3600; // 1 hour in seconds
    const refreshIn = Math.max(0, (timeUntilExpiration - refreshBuffer) * 1000);

    console.log(`Scheduling token refresh in ${Math.floor(refreshIn / 1000 / 60)} minutes`);

    this.refreshTimer = setTimeout(() => {
      console.log('Auto-refreshing token...');
      this.refreshToken().subscribe({
        next: () => {
          console.log('Token auto-refreshed successfully');
          this.scheduleTokenRefresh(); // Schedule next refresh
        },
        error: (error) => {
          console.error('Auto-refresh failed:', error);
          // Don't logout on auto-refresh failure, user might be offline
          // The error interceptor will handle 401s when they make requests
        }
      });
    }, refreshIn);
  }

  loginWithGoogle(idToken: string): Observable<AuthResponse> {
    return this.api.post<AuthResponse>('/auth/google', { idToken }).pipe(
      tap((response) => {
        this.storage.setToken(response.token);
        this.storage.setUser(response.user);
        this.currentUserSubject.next(response.user);
        this.scheduleTokenRefresh();
      })
    );
  }

  logout(): void {
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    this.api.post('/auth/logout').subscribe();
    this.storage.clear();
    this.currentUserSubject.next(null);
  }

  /**
   * Refresh the authentication token
   */
  refreshToken(): Observable<AuthResponse> {
    // Prevent multiple simultaneous refresh requests
    if (this.isRefreshing) {
      return of({} as AuthResponse);
    }

    this.isRefreshing = true;

    return this.api.post<AuthResponse>('/auth/refresh', {}).pipe(
      tap((response) => {
        this.storage.setToken(response.token);
        this.storage.setUser(response.user);
        this.currentUserSubject.next(response.user);
        this.isRefreshing = false;
        this.scheduleTokenRefresh();
      }),
      catchError((error) => {
        this.isRefreshing = false;
        throw error;
      })
    );
  }

  getToken(): string | null {
    return this.storage.getToken();
  }

  isAuthenticated(): boolean {
    return !!this.storage.getToken();
  }

  getCurrentUser(): Observable<User | null> {
    return this.currentUser$;
  }

  refreshUser(): Observable<User> {
    return this.api.get<User>('/auth/me').pipe(
      tap((user) => {
        this.storage.setUser(user);
        this.currentUserSubject.next(user);
      })
    );
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  checkAndRefreshToken(): Observable<boolean> {
    if (this.storage.willTokenExpireSoon(60)) {
      return this.refreshToken().pipe(
        map(() => true),
        catchError(() => of(false))
      );
    }
    return of(true);
  }
}
